// Must run before tsParticles creates its canvas (old iOS has no OffscreenCanvas)
import './compat.js';
import { tsParticles } from '@tsparticles/engine';
import { loadAll } from '@tsparticles/all';
import { AppState } from './state.js';
import { UIManager } from './uiManager.js';
import { ModalManager } from './modalManager.js';
import { initTooltipManager } from './tooltipManager.js';
import { CommandManager } from './commandManager.js';
import { debounce } from './utils.js';
import { ErrorHandler, ErrorType } from './errorHandler.js';
import { StateManager, Actions } from './stateManager.js';
import { BUTTON_IDS, AUTO_HIDE_DELAY, STORAGE_KEYS } from './constants.js';
import { SafeStorage } from './storage.js';
import { Telemetry } from './telemetry.js';
import {
  buildConfig,
  loadParticles,
  applyCursorMode,
  applyWallsMode,
  applyGravityMode,
  updateThemeAndReload,
  onSceneLoaded,
} from './particlesService.js';
import {
  initKeyboardShortcuts,
  isButtonDisabled,
} from './keyboardShortcuts.js';
import {
  createShuffleCommand,
  createToggleCommand,
  createThemeCommand,
} from './commandFactory.js';
import { handleShareClick } from './shareManager.js';
import {
  loadSavedConfig,
  loadConfigFromHash,
  restoreSavedToggleState,
  loadSavedAdvancedSettings,
} from './configLoader.js';
import { setupInfoModalTabs } from './infoModalManager.js';
import { createDrift } from './drift.js';
import { createSoundController } from './sound/soundController.js';

// Dev-only: expose key state on window for debugging in the console.
// `import.meta.env.DEV` is replaced at build time by Vite, so this block
// is stripped from production bundles.
if (import.meta.env?.DEV) {
  window.AppState = AppState;
  window.tsParticles = tsParticles;
}

// Main async function to encapsulate the entire application logic.
(async () => {
  try {
    // Initialize tsParticles with error handling
    await loadAll(tsParticles);
  } catch (error) {
    console.error('Failed to load tsParticles library:', error);
    ErrorHandler.createFatalErrorUI(ErrorType.LIBRARY_LOAD, error);
    return;
  }

  // Advanced preferences must be in place before their switches are wired
  loadSavedAdvancedSettings();

  // --- 1. ELEMENT SELECTORS ---
  const mainMenuBtn = document.getElementById(BUTTON_IDS.MAIN_MENU);
  const menuContainer = document.getElementById('menu-container');
  const subMenu = document.getElementById('sub-menu');
  const chaosSlider = document.getElementById('chaos-slider');
  const infoModal = document.getElementById('info-modal');
  const closeInfoModalBtn = document.getElementById('close-info-modal');
  const btnInfo = document.getElementById(BUTTON_IDS.INFO);
  const shuffleAllBtn = document.getElementById(BUTTON_IDS.SHUFFLE_ALL);
  const fullscreenBtn = document.getElementById('fullscreen-btn');

  // --- 2. TOOLTIP SETUP ---
  initTooltipManager(subMenu);

  // --- 3. CORE LOGIC FUNCTIONS ---

  /** Handles the logic for toggling the application's color theme. */
  const updateTheme = async () => {
    StateManager.dispatch(Actions.setTheme(!AppState.ui.isDarkMode));
    StateManager.persist();
    const themeMessage = AppState.ui.isDarkMode
      ? 'Dark theme enabled'
      : 'Light theme enabled';
    UIManager.announce(themeMessage);
    UIManager.showToast(themeMessage);
    await updateThemeAndReload();
  };

  /** Toggles fullscreen mode for the browser window. */
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .catch((err) => console.error(err));
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  // ModalManager now imported as a module

  // --- 4. COMMAND PATTERN IMPLEMENTATION ---
  // Command factories are now in commandFactory.js for testability

  /** Helper to create shuffle command with injected dependencies */
  const makeShuffleCommand = (shuffleOptions) =>
    createShuffleCommand({
      shuffleOptions,
      appState: AppState,
      uiManager: UIManager,
      buildConfig,
      loadParticles,
    });

  /** Helper to create toggle command with injected dependencies */
  const makeToggleCommand = (stateKey, applyFn) =>
    createToggleCommand({
      stateKey,
      appState: AppState,
      uiManager: UIManager,
      applyFn,
    });

  /** Helper to create theme command with injected dependencies */
  const makeThemeCommand = () => createThemeCommand({ updateTheme });

  // --- 5. EVENT LISTENERS ---

  // Auto-hide menu after 10 seconds of inactivity
  let menuInactivityTimer = null;

  const QuickStartStep = Object.freeze({
    COG: 'cog',
    SHUFFLE: 'shuffle',
    INFO: 'info',
    DONE: 'done',
  });

  let quickStartStep = QuickStartStep.DONE;

  const quickStartTargets = [
    { element: mainMenuBtn, accentClass: 'quickstart-cog', label: '1 Menu' },
    {
      element: shuffleAllBtn,
      accentClass: 'quickstart-shuffle',
      label: '2 Shuffle',
    },
    { element: btnInfo, accentClass: 'quickstart-info', label: '3 Info' },
  ];

  const clearQuickStartHighlights = () => {
    quickStartTargets.forEach(({ element, accentClass }) => {
      if (!element) return;
      element.classList.remove('quickstart-target', accentClass);
      element.removeAttribute('data-guide-label');
    });
  };

  const setQuickStartHighlight = (targetElement, accentClass, label) => {
    clearQuickStartHighlights();
    if (!targetElement) return;
    targetElement.classList.add('quickstart-target', accentClass);
    targetElement.setAttribute('data-guide-label', label);
  };

  const completeQuickStart = () => {
    quickStartStep = QuickStartStep.DONE;
    clearQuickStartHighlights();
    SafeStorage.setItem(STORAGE_KEYS.QUICKSTART_DONE, 'true');
    // Migration: if a user had already dismissed the old modal, keep parity.
    SafeStorage.setItem(STORAGE_KEYS.WELCOME_DISMISSED, 'true');
  };

  const setQuickStartStep = (nextStep) => {
    quickStartStep = nextStep;

    if (nextStep === QuickStartStep.COG) {
      setQuickStartHighlight(mainMenuBtn, 'quickstart-cog', '1 Menu');
    } else if (nextStep === QuickStartStep.SHUFFLE) {
      setQuickStartHighlight(shuffleAllBtn, 'quickstart-shuffle', '2 Shuffle');
    } else if (nextStep === QuickStartStep.INFO) {
      setQuickStartHighlight(btnInfo, 'quickstart-info', '3 Info');
    } else {
      clearQuickStartHighlights();
    }
  };

  const resetMenuInactivityTimer = () => {
    clearTimeout(menuInactivityTimer);
    if (menuContainer.classList.contains('active')) {
      menuInactivityTimer = setTimeout(() => {
        if (menuContainer.classList.contains('active')) {
          mainMenuBtn.click(); // Close the menu
        }
      }, AUTO_HIDE_DELAY);
    }
  };

  // Track menu interaction to reset inactivity timer
  menuContainer.addEventListener('mouseenter', resetMenuInactivityTimer);
  menuContainer.addEventListener('mousemove', resetMenuInactivityTimer);
  menuContainer.addEventListener('click', resetMenuInactivityTimer);
  menuContainer.addEventListener('touchstart', resetMenuInactivityTimer);

  mainMenuBtn.addEventListener('click', () => {
    const isActive = menuContainer.classList.toggle('active');
    mainMenuBtn.setAttribute('aria-pressed', isActive);
    mainMenuBtn.setAttribute(
      'aria-label',
      isActive ? 'Close Settings Menu' : 'Open Settings Menu'
    );

    if (quickStartStep === QuickStartStep.COG && isActive) {
      setQuickStartStep(QuickStartStep.SHUFFLE);
    }

    if (isActive) {
      document.getElementById(BUTTON_IDS.SHUFFLE_ALL).focus();
      resetMenuInactivityTimer(); // Start auto-hide timer
    } else {
      mainMenuBtn.focus(); // Return focus on close
      clearTimeout(menuInactivityTimer); // Clear timer when closed manually
    }
  });

  /** Main event listener for the control panel, using event delegation for performance. */
  subMenu.addEventListener('click', (e) => {
    const button = e.target.closest('.menu-button');
    // Disabled buttons only block pointer events in CSS; keyboard shortcuts
    // and Enter/Space still reach here via .click().
    if (!button || isButtonDisabled(button)) return;

    switch (button.id) {
      case BUTTON_IDS.SHUFFLE_ALL:
        CommandManager.execute(makeShuffleCommand({ all: true }));
        if (quickStartStep === QuickStartStep.SHUFFLE) {
          setQuickStartStep(QuickStartStep.INFO);
        }
        break;
      case BUTTON_IDS.SHUFFLE_APPEARANCE:
        CommandManager.execute(makeShuffleCommand({ appearance: true }));
        break;
      case BUTTON_IDS.SHUFFLE_MOVEMENT:
        CommandManager.execute(makeShuffleCommand({ movement: true }));
        break;
      case BUTTON_IDS.SHUFFLE_INTERACTION:
        CommandManager.execute(makeShuffleCommand({ interaction: true }));
        break;
      case BUTTON_IDS.SHUFFLE_FX:
        CommandManager.execute(makeShuffleCommand({ fx: true }));
        break;
      case BUTTON_IDS.BACK:
        CommandManager.undo();
        break;
      case BUTTON_IDS.FORWARD:
        CommandManager.redo();
        break;
      case BUTTON_IDS.THEME:
        CommandManager.execute(makeThemeCommand());
        break;
      case BUTTON_IDS.GRAVITY:
        CommandManager.execute(
          makeToggleCommand('isGravityOn', async () => {
            applyGravityMode();
            await loadParticles(AppState.particleState.currentConfig);
            UIManager.showToast(
              `Gravity ${AppState.ui.isGravityOn ? 'enabled' : 'disabled'}`
            );
          })
        );
        break;
      case BUTTON_IDS.WALLS:
        CommandManager.execute(
          makeToggleCommand('areWallsOn', async () => {
            applyWallsMode();
            await loadParticles(AppState.particleState.currentConfig);
            UIManager.showToast(
              `Walls ${AppState.ui.areWallsOn ? 'enabled' : 'disabled'}`
            );
          })
        );
        break;
      case BUTTON_IDS.CURSOR:
        CommandManager.execute(
          makeToggleCommand('isCursorParticle', async () => {
            applyCursorMode();
            await loadParticles(AppState.particleState.currentConfig);
            UIManager.showToast(
              `Cursor particle ${
                AppState.ui.isCursorParticle ? 'enabled' : 'disabled'
              }`
            );
          })
        );
        break;
      case BUTTON_IDS.REFRESH: {
        const config = AppState.particleState.currentConfig;
        if (config && Object.keys(config).length > 0) {
          loadParticles(config)
            .then(() => {
              UIManager.showToast('Scene refreshed!');
              UIManager.announce('Scene refreshed');
            })
            .catch((error) => {
              ErrorHandler.handle(error, ErrorType.PARTICLES_LOAD, {
                action: 'refresh',
              });
            });
        }
        break;
      }
      case BUTTON_IDS.PAUSE:
        (() => {
          const container = AppState.ui.particlesContainer;
          if (!container) {
            UIManager.showToast('No particle animation loaded');
            UIManager.announce('No particle animation loaded');
            return;
          }
          StateManager.dispatch(Actions.togglePause());
          if (AppState.ui.isPaused) container.pause();
          else container.play();
          sound.onPauseChange();
          UIManager.syncUI();
          const pauseMessage = AppState.ui.isPaused
            ? 'Animation paused'
            : 'Animation resumed';
          UIManager.announce(pauseMessage);
          UIManager.showToast(pauseMessage);
        })();
        break;
      case BUTTON_IDS.SHARE:
        handleShareClick(button).catch((error) => {
          ErrorHandler.handle(error, ErrorType.SHARE_FAILED);
        });
        break;
      case BUTTON_IDS.INFO:
        UIManager.populateInfoModal();
        ModalManager.open('info', button);
        if (quickStartStep === QuickStartStep.INFO) {
          completeQuickStart();
        }
        break;
    }
  });

  // Create debounced functions for chaos slider
  const debouncedChaosAnnounce = debounce((level) => {
    UIManager.announce(`Chaos level ${level}`);
  }, 300);

  const debouncedChaosSave = debounce((level) => {
    SafeStorage.setItem(STORAGE_KEYS.CHAOS, String(level));
  }, 500);

  chaosSlider.addEventListener('input', (e) => {
    const newValue = parseInt(e.target.value, 10);
    // Validate range
    if (newValue < 1 || newValue > 10 || isNaN(newValue)) {
      console.warn('Invalid chaos level:', newValue);
      return;
    }
    StateManager.dispatch(Actions.setChaosLevel(newValue));
    UIManager.syncUI();
    // Use debounced versions to prevent excessive calls
    debouncedChaosAnnounce(AppState.particleState.chaosLevel);
    debouncedChaosSave(AppState.particleState.chaosLevel);
  });

  chaosSlider.addEventListener('change', () => {
    // Show toast and announce on change (when user releases the slider)
    const chaosMessage = `Chaos level set to ${AppState.particleState.chaosLevel}`;
    UIManager.showToast(chaosMessage);
    UIManager.announce(chaosMessage);
  });

  /** Wire up the advanced settings toggles within the info modal. */
  const setupAdvancedSettingsPanel = () => {
    const toggleInputs = document.querySelectorAll('[data-advanced-setting]');
    const resetBtn = document.getElementById('advanced-reset-btn');
    const copyBtn = document.getElementById('copy-diagnostics-btn');

    if (!toggleInputs.length) return;

    const messages = {
      autoPauseHidden: {
        enabled: 'Auto pause when hidden enabled',
        disabled: 'Auto pause when hidden disabled',
      },
      batterySaverMode: {
        enabled: 'Battery saver mode enabled',
        disabled: 'Battery saver mode disabled',
      },
      gentleDrift: {
        enabled: 'Gentle drift on',
        disabled: 'Gentle drift off',
      },
      sound: {
        enabled: 'Sound on',
        disabled: 'Sound off',
      },
    };

    // Settings that don't change how the current scene is drawn
    const noReload = new Set(['gentleDrift', 'sound']);

    const syncToggleStates = () => {
      toggleInputs.forEach((input) => {
        const key = input.dataset.advancedSetting;
        if (!key) return;
        input.checked = Boolean(AppState.advanced[key]);
      });
    };

    const reloadIfReady = async () => {
      const config = AppState.particleState.currentConfig;
      if (config && Object.keys(config).length > 0) {
        await loadParticles(config);
      }
    };

    toggleInputs.forEach((input) => {
      const key = input.dataset.advancedSetting;
      if (!key) return;
      input.checked = Boolean(AppState.advanced[key]);

      input.addEventListener('change', async () => {
        StateManager.dispatch(Actions.setAdvancedSetting(key, input.checked));
        const status = input.checked ? 'enabled' : 'disabled';
        const message = messages[key] ? messages[key][status] : null;
        if (message) {
          UIManager.showToast(message);
          UIManager.announce(message);
        }
        Telemetry.log('advanced_setting', { key, value: input.checked });
        drift?.restart();
        // The switch's change event is the gesture browsers need to start audio
        if (key === 'sound') sound.setEnabled(input.checked);
        if (!noReload.has(key)) await reloadIfReady();
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', async () => {
        StateManager.dispatch(Actions.resetAdvancedSettings());
        syncToggleStates();
        UIManager.showToast('Advanced settings restored');
        UIManager.announce('Advanced settings restored');
        Telemetry.log('advanced_setting_reset');
        drift?.restart();
        sound.setEnabled(AppState.advanced.sound);
        await reloadIfReady();
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        try {
          await Telemetry.copyToClipboard();
          UIManager.showToast('Diagnostics log copied');
          UIManager.announce('Diagnostics log copied');
          Telemetry.log('observability:log-exported', {
            entries: Telemetry.getEvents().length,
          });
        } catch (error) {
          ErrorHandler.handle(error, ErrorType.UNKNOWN, {
            feature: 'copy-diagnostics',
          });
        }
      });
    }
  };

  // --- SOUND (beta) ---
  const sound = createSoundController({
    getScene: () => ({
      config: AppState.particleState.currentConfig,
      chaosLevel: AppState.particleState.chaosLevel,
      isDarkMode: AppState.ui.isDarkMode,
    }),
    isPaused: () => AppState.ui.isPaused,
  });
  onSceneLoaded(() => sound.onSceneChange());
  // Taps on the scene itself (not the menu or modals) play notes
  document.addEventListener('pointerdown', (e) => {
    if (e.target.closest?.('#tsparticles')) sound.onTap(e.clientX, e.clientY);
  });

  // --- GENTLE DRIFT ---
  // Created after the first scene loads (see initialisation below).
  let drift = null;
  const startDrift = () => {
    drift = createDrift({
      isEnabled: () => AppState.advanced.gentleDrift,
      canDrift: () =>
        !AppState.ui.isPaused &&
        !document.hidden &&
        !ModalManager.isOpen('info') &&
        Boolean(AppState.ui.particlesContainer),
      // One quiet aspect at a time: how it looks, or how it moves
      step: () => {
        const aspect = Math.random() < 0.5 ? 'appearance' : 'movement';
        CommandManager.execute(makeShuffleCommand({ [aspect]: true }));
        Telemetry.log('drift:step', { aspect });
      },
    });
  };

  // Register all modals with the ModalManager
  ModalManager.register('info', infoModal, closeInfoModalBtn);

  // Setup tab functionality for info modal
  setupInfoModalTabs(infoModal, btnInfo);
  setupAdvancedSettingsPanel();

  fullscreenBtn.addEventListener('click', toggleFullScreen);
  document.addEventListener(
    'fullscreenchange',
    UIManager.updateFullscreenIcons
  );

  // Tooltip logic moved into tooltipManager module

  // --- 6. ACCESSIBILITY - KEYBOARD ACTIVATION & SHORTCUTS ---
  document.querySelectorAll('[role="button"]').forEach((button) => {
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        button.click();
      }
    });
  });

  chaosSlider.addEventListener('keydown', (e) => {
    const value = parseInt(chaosSlider.value, 10);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      chaosSlider.value = Math.max(1, value - 1);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      chaosSlider.value = Math.min(10, value + 1);
    } else {
      return;
    }
    e.preventDefault();
    chaosSlider.dispatchEvent(new Event('input', { bubbles: true }));
  });

  initKeyboardShortcuts(menuContainer);

  // --- KONAMI CODE EASTER EGG ---
  /** Special particle configuration for Konami code (↑ ↑ ↓ ↓ ← → ← → B A) */
  const konamiCode = [
    'ArrowUp',
    'ArrowUp',
    'ArrowDown',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowLeft',
    'ArrowRight',
    'b',
    'a',
  ];
  let konamiIndex = 0;

  document.addEventListener('keydown', (e) => {
    const key = e.key.toLowerCase();
    const expectedKey = konamiCode[konamiIndex].toLowerCase();

    if (key === expectedKey || e.key === expectedKey) {
      konamiIndex++;
      if (konamiIndex === konamiCode.length) {
        konamiIndex = 0;
        // Trigger special "party mode" configuration
        const partyConfig = buildConfig({ all: true });
        partyConfig.particles.number.value = 300;
        partyConfig.particles.paint.fill.color = { value: 'random' };
        partyConfig.particles.move.speed = 10;
        partyConfig.particles.shape.type = ['star', 'circle', 'triangle'];
        partyConfig.particles.size.value = { min: 2, max: 8 };
        partyConfig.particles.move.direction = 'none';
        partyConfig.particles.move.random = true;
        partyConfig.particles.move.outModes = { default: 'bounce' };

        const prevConfig = structuredClone(
          AppState.particleState.currentConfig
        );
        CommandManager.execute({
          newConfig: partyConfig,
          shuffleType: '🎉 Party Mode',
          async execute() {
            await loadParticles(partyConfig);
            StateManager.dispatch(Actions.setConfig(partyConfig));
            UIManager.syncUI();
            UIManager.showToast('🎉 Party Mode Activated! 🎊');
            UIManager.announce('Party mode activated with Konami code');
          },
          async undo() {
            await loadParticles(prevConfig);
            UIManager.syncUI();
          },
        });
      }
    } else {
      konamiIndex = 0;
    }
  });

  // --- 7. INITIALISATION ---
  /** This section sets up the initial state of the application on load. */
  const savedTheme = SafeStorage.getItem(STORAGE_KEYS.THEME);
  StateManager.dispatch(
    Actions.setTheme(savedTheme ? savedTheme === 'dark' : true)
  );

  const savedChaos = SafeStorage.getItem(STORAGE_KEYS.CHAOS);
  StateManager.dispatch(
    Actions.setChaosLevel(savedChaos ? parseInt(savedChaos, 10) : 5)
  );

  if (SafeStorage.usingFallback()) {
    UIManager.announce(
      'Storage is unavailable — your scene and settings will not persist after reload.'
    );
    console.warn(
      'SafeStorage: localStorage unavailable, using in-memory fallback. Scene and settings will not persist.'
    );
  }

  const initialConfigFromStorage = loadSavedConfig();
  const loadedFromUrl = loadConfigFromHash();
  // A shared link carries its own toggles in uiState; otherwise restore the
  // toggles that the saved scene was built with.
  if (!loadedFromUrl && initialConfigFromStorage) {
    restoreSavedToggleState();
  }

  // Determine initial config: URL config > saved config > fresh random shuffle
  let configToLoad =
    AppState.particleState.initialConfigFromUrl || initialConfigFromStorage;

  // If no saved or URL config, generate a fresh random shuffle
  let freshConfig = null;
  if (!configToLoad) {
    freshConfig = buildConfig({ all: true });
    configToLoad = freshConfig;
  }

  await loadParticles(configToLoad);

  // A saved or shared scene that fails to load gets one fresh shuffle; if
  // even that fails, the browser can't run the engine.
  if (!AppState.ui.particlesContainer && configToLoad !== freshConfig) {
    await loadParticles(buildConfig({ all: true }));
  }
  if (!AppState.ui.particlesContainer) {
    ErrorHandler.createFatalErrorUI(ErrorType.BROWSER_UNSUPPORTED);
    return;
  }

  UIManager.syncUI();
  startDrift();
  // Sound left on from a previous visit waits, silent, for the first tap
  if (AppState.advanced.sound) sound.setEnabled(true);

  // Start a minimal inline quick-start unless already completed.
  const quickStartDone = SafeStorage.getItem(STORAGE_KEYS.QUICKSTART_DONE);
  const welcomeDismissed = SafeStorage.getItem(STORAGE_KEYS.WELCOME_DISMISSED);
  if (quickStartDone === 'true' || welcomeDismissed === 'true') {
    completeQuickStart();
  } else {
    setQuickStartStep(QuickStartStep.COG);
  }

  // --- 8. REDUCED MOTION HANDLING ---
  // tsDice is fundamentally a motion-based app — silently auto-pausing when
  // the OS has `prefers-reduced-motion: reduce` makes it look broken (the user
  // sees particles load and respond to hover/click but not drift).
  // Instead: surface a one-time hint and let the user decide. Space toggles
  // pause if they want it.
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (motionQuery.matches) {
    UIManager.announce(
      'Reduced motion preference detected. Press Space to pause if desired.'
    );
  }

  // --- 9. DEBOUNCED WINDOW RESIZE ---
  const handleResize = debounce(() => {
    const container = AppState.ui.particlesContainer;
    if (container) {
      container.refresh();
    }
  }, 250);
  window.addEventListener('resize', handleResize);

  // --- 10. MEMORY LEAK PREVENTION ---
  /** Cleanup function to prevent memory leaks on page unload. Also flushes
   *  any pending debounced persist so the user's last change isn't lost. */
  window.addEventListener('beforeunload', () => {
    StateManager.cancelPendingPersist();
    StateManager.persist();
    const container = AppState.ui.particlesContainer;
    if (container) {
      container.destroy();
    }
  });
})();
